import React, { ComponentType } from "react";
import { useLocation, useNavigate, useParams, Location, NavigateFunction, Params } from "react-router";

/**
 * Props injected by the withRouter HOC into wrapped components.
 * Provides react-router v7 hooks as props for compatibility with class components.
 */
export interface WithRouterProps {
  location: Location;
  history: NavigateFunction;
  match: Readonly<Params<string>>;
}

/**
 * Higher-order component that injects react-router hooks as props.
 * Useful for class components or components that need router props.
 *
 * Injected props:
 * - location: Current location object from useLocation()
 * - history: Navigate function from useNavigate() (for backwards compatibility with older react-router APIs)
 * - match: URL params object from useParams()
 *
 * @template P - The props type of the wrapped component
 * @param Component - Component to wrap with router props
 * @returns Wrapped component with router props injected
 *
 * @example
 * interface MyComponentProps {
 *   name: string;
 * }
 *
 * class MyComponent extends React.Component<MyComponentProps & WithRouterProps> {
 *   handleClick = () => {
 *     this.props.history('/home');
 *   }
 * }
 *
 * export default withRouter(MyComponent);
 */
export const withRouter = <P extends object>(
  Component: ComponentType<P & WithRouterProps>
): ComponentType<P> => {
  const WithRouter = (props: P) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return (
      <Component
        {...props}
        location={location}
        history={navigate}
        match={params}
      />
    );
  };

  WithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  return WithRouter;
};
